/**
 * Knowledge Base
 *
 * Central export for all documentation content.
 * Used by Help.tsx for the UI and by the Virtual CISO chatbot for context.
 */

import {
  KnowledgeArticle,
  KnowledgeSection,
  KnowledgeBase,
  buildAIContext,
} from './types'

// Import all article collections
import { gettingStartedArticles } from './articles/getting-started'
import { controlsArticles } from './articles/controls'
import { documentsArticles } from './articles/documents'
import { tasksArticles } from './articles/tasks'
import { risksArticles } from './articles/risks'
import { krisArticles } from './articles/kris'
import { darkWebArticles } from './articles/dark-web'
import { tprmArticles } from './articles/tprm'
import { scannersArticles } from './articles/scanners'
import { assetsArticles } from './articles/assets'
import { integrationsArticles } from './articles/integrations'
import { usersArticles } from './articles/users'
import { settingsArticles } from './articles/settings'

// Define sections with their metadata
const sections: KnowledgeSection[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: 'Book',
    description: 'Introduction to the GRC platform and quick start guides',
    order: 1,
    articles: gettingStartedArticles,
  },
  {
    id: 'controls',
    title: 'Controls Management',
    icon: 'Shield',
    description: 'Managing compliance controls and uploading evidence',
    order: 2,
    articles: controlsArticles,
  },
  {
    id: 'documents',
    title: 'Documents & Policies',
    icon: 'FileText',
    description: 'Creating and managing policies, procedures, and standards',
    order: 3,
    articles: documentsArticles,
  },
  {
    id: 'tasks',
    title: 'Task Management',
    icon: 'CheckSquare',
    description: 'Tracking compliance activities and Jira integration',
    order: 4,
    articles: tasksArticles,
  },
  {
    id: 'risks',
    title: 'Risk Management',
    icon: 'AlertTriangle',
    description: 'Risk register, assessment, and treatment',
    order: 5,
    articles: risksArticles,
  },
  {
    id: 'kris',
    title: 'Key Risk Indicators',
    icon: 'Activity',
    description: 'KRI monitoring, thresholds, and alerts',
    order: 6,
    articles: krisArticles,
  },
  {
    id: 'dark-web',
    title: 'Dark Web Monitoring',
    icon: 'Skull',
    description: 'Credential exposure and breach monitoring',
    order: 7,
    articles: darkWebArticles,
  },
  {
    id: 'tprm',
    title: 'Vendor Risk Management',
    icon: 'Building2',
    description: 'Third-party risk assessments and monitoring',
    order: 8,
    articles: tprmArticles,
  },
  {
    id: 'scanners',
    title: 'Security Scanning',
    icon: 'Radar',
    description: 'Vulnerability scanning and results management',
    order: 9,
    articles: scannersArticles,
  },
  {
    id: 'assets',
    title: 'Asset Management',
    icon: 'Server',
    description: 'IT asset inventory and lifecycle management',
    order: 10,
    articles: assetsArticles,
  },
  {
    id: 'integrations',
    title: 'Cloud Integrations',
    icon: 'Cloud',
    description: 'AWS, Azure, and Atlassian integration setup',
    order: 11,
    articles: integrationsArticles,
  },
  {
    id: 'users',
    title: 'User Management',
    icon: 'Users',
    description: 'User roles, permissions, and account management',
    order: 12,
    articles: usersArticles,
  },
  {
    id: 'settings',
    title: 'Settings',
    icon: 'Settings',
    description: 'Organization settings, integrations, and audit logs',
    order: 13,
    articles: settingsArticles,
  },
]

// Collect all articles into a flat array
const allArticles: KnowledgeArticle[] = sections.flatMap((s) => s.articles)

// Build the article map for quick lookup
const articleMap: Record<string, KnowledgeArticle> = {}
for (const article of allArticles) {
  articleMap[article.id] = article
}

// Build AI context for chatbot use
const aiContext = buildAIContext(allArticles)

/**
 * The complete knowledge base with all documentation content.
 * Import this for full access to sections, articles, and AI context.
 */
export const knowledgeBase: KnowledgeBase = {
  sections,
  articleMap,
  aiContext,
}

// Named exports for convenience
export { sections, articleMap, allArticles, aiContext }

// Re-export types
export type { KnowledgeArticle, KnowledgeSection, KnowledgeBase }

/**
 * Find articles relevant to a page path.
 * Used by the chatbot to inject context based on current page.
 */
export function getArticlesForPage(pagePath: string): KnowledgeArticle[] {
  const articleIds = aiContext.pageMap[pagePath] || []
  return articleIds.map((id) => articleMap[id]).filter(Boolean)
}

/**
 * Find articles matching a search query.
 * Searches titles, summaries, and tags.
 */
export function searchArticles(query: string): KnowledgeArticle[] {
  const lowerQuery = query.toLowerCase()

  return allArticles.filter((article) => {
    const titleMatch = article.title.toLowerCase().includes(lowerQuery)
    const summaryMatch = article.summary.toLowerCase().includes(lowerQuery)
    const tagMatch = article.tags.some((tag) =>
      tag.toLowerCase().includes(lowerQuery)
    )
    const contentMatch = article.content.toLowerCase().includes(lowerQuery)

    return titleMatch || summaryMatch || tagMatch || contentMatch
  })
}

/**
 * Get articles by tag.
 */
export function getArticlesByTag(tag: string): KnowledgeArticle[] {
  const articleIds = aiContext.tagMap[tag] || []
  return articleIds.map((id) => articleMap[id]).filter(Boolean)
}

/**
 * Get the AI context summary for a set of page paths.
 * Used by chatbot to build relevant context.
 */
export function getAIContextForPages(pagePaths: string[]): string {
  const relevantArticleIds = new Set<string>()

  for (const path of pagePaths) {
    const articleIds = aiContext.pageMap[path] || []
    articleIds.forEach((id) => relevantArticleIds.add(id))
  }

  const relevantArticles = Array.from(relevantArticleIds)
    .map((id) => articleMap[id])
    .filter(Boolean)

  return relevantArticles.map((a) => `[${a.title}]: ${a.summary}`).join('\n')
}
